import {journalEntryLevelConstants, journalEntrySyncStatusConstants, suggestionRecordTypeConstants} from '../../constants';

/**
 * Pure logic for aggregated journal entries: one header row that stands for a month's receipt
 * expenses, and its members (one per expense). Kept free of JSX and component imports so it can be
 * unit tested; `accounting.helpers.js` cannot be imported by the test runner.
 *
 * A header is a list row with a `member_count`; its members carry `aggregate_group` too, so the
 * group alone does not tell them apart. A header's members are fetched when the row first opens
 * and kept on the row as `aggregate_members`, with `aggregate_members_status` saying where the
 * fetch is. `aggregate_applying` is set while "Apply to empty lines" is running. The table's
 * `children` are derived from these at format time, never stored.
 */

export const AGGREGATE_MEMBERS_STATUS = Object.freeze({
  LOADING: 'loading',
  LOADED: 'loaded',
  ERROR: 'error',
});

const SKELETON_ROW_COUNT = 3;


const LOCATION_SEGMENT = 'location';

/** The key of the explanatory row under a header. */
export const getNoteRowId = (headerId) => `${headerId}-note`;

/** The key of one loading placeholder row under a header. */
export const getSkeletonRowId = (headerId, index) => `${headerId}-skeleton-${index}`;

/** Most member fetches in flight at once when the list prefetches the headers on screen. */
export const MEMBER_PREFETCH_LIMIT = 4;

/**
 * Whether the row is an aggregated journal entry header. Members carry `aggregate_group` too, so the
 * group does not tell them apart: only a header has a `member_count`, as in the backend.
 */
export const isAggregateHeader = (entry) => entry?.member_count !== null && entry?.member_count !== undefined;

/** Whether the row is one expense of an aggregated header. */
export const isAggregateMember = (entry) => Boolean(entry?.isAggregateMember);

/** Whether the row sits under a header: a member, the explanatory row or a loading placeholder. */
export const isAggregateChildRow = (entry) =>
  isAggregateMember(entry) || Boolean(entry?.isAggregateNote) || Boolean(entry?.isAggregateSkeleton);

/** A header is ready to sync when the backend reports no blocker; the blocker is the tooltip. */
export const isHeaderReadyToSync = (entry) => !entry?.blocking_reason;

/** The header's credit account: the stored credit line is always first, even with a zero amount. */
export const getHeaderCreditAccount = (entry) => entry?.lines?.[0]?.account ?? null;

const toLevels = (levels) => {
  if (Array.isArray(levels)) return levels;
  return levels ? [levels] : [];
};

/**
 * Whether a custom segment can be set on a header row. A segment with levels is a header segment
 * only when those include the header level; a segment without levels is a plain field of the row.
 * @example
 * segmentAppliesToHeader(['L']); // false
 * segmentAppliesToHeader(['H', 'L']); // true
 */
export const segmentAppliesToHeader = (levels) => {
  const list = toLevels(levels);
  return !list.length || list.includes(journalEntryLevelConstants.HEADER);
};

/** The value of a custom segment on a header row: header segments live in `custom_segments`. */
export const getHeaderSegmentValue = ({levels, name, record}) => {
  const list = toLevels(levels);
  if (!list.length) return record?.[name] || undefined;
  if (!list.includes(journalEntryLevelConstants.HEADER)) return undefined;
  return record?.custom_segments?.[name] || undefined;
};

/** The levels a header edit may write: only the header level, never a member's line. */
export const getHeaderUpdateLevels = (levels) =>
  toLevels(levels).filter((level) => level === journalEntryLevelConstants.HEADER);

/** The members as the API returned them, or none while they are not loaded. */
export const getMembers = (header) => header?.aggregate_members ?? [];

const hasValue = (value) => value !== null && value !== undefined && value !== '';

/** The segments an expense is chosen for one by one: the line-level ones, Department included. */
export const getMemberSegments = (customSegments = []) =>
  customSegments.filter((segment) => {
    const levels = toLevels(segment.levels);
    return levels.length > 0
      && !levels.includes(journalEntryLevelConstants.HEADER);
  });

/**
 * Whether the company's Location is a line-level segment, from the raw segments the page loads
 * (`setup.internal_id` and `setup[levelsPropName]`), as opposed to the mapped columns of a tab.
 */
export const isLocationLineLevel = ({customSegments = [], levelsPropName}) =>
  customSegments.some((segment) => segment?.setup?.internal_id === LOCATION_SEGMENT
    && getMemberSegments([{name: LOCATION_SEGMENT, levels: segment.setup?.[levelsPropName]}]).length > 0);

/**
 * A member's value in a column: its debit account, or a line-level segment (Department included)
 * read from the debit line's segments.
 * @param {Object} member - An expense of a header.
 * @param {string} field - `debit_account` or a segment id.
 */
export const getMemberFieldValue = (member, field) => {
  if (field === 'debit_account') return member?.[field];
  return member?.line_segments?.[field];
};

/**
 * Whether a member has a value in a column. A value the agent filled is stored like any other, and
 * a suggestion the agent made for the field counts too, so neither is ever overwritten.
 */
export const isMemberFieldSet = (member, field) =>
  hasValue(getMemberFieldValue(member, field)) || hasValue(member?.agent_suggestion?.suggestions?.[field]?.value);

/**
 * How many members have a value in a column.
 * @returns {{set: number, total: number}|null} null until the members are loaded.
 */
export const getFieldSetCount = (header, field) => {
  if (header?.aggregate_members_status !== AGGREGATE_MEMBERS_STATUS.LOADED) return null;
  const members = getMembers(header);
  return {set: members.filter((member) => isMemberFieldSet(member, field)).length, total: members.length};
};

/**
 * How many members have a debit account and a department.
 * @returns {{debit: {set: number, total: number}, department: {set: number, total: number}}|null}
 *   null until the members are loaded.
 */
export const getSetCounts = (header) => {
  const debit = getFieldSetCount(header, 'debit_account');
  if (!debit) return null;
  return {debit, department: getFieldSetCount(header, 'department')};
};

/** Ids of the members with no value in a column yet: the ones "Apply to empty lines" fills. */
export const getEmptyMemberIds = (header, field) =>
  getMembers(header).filter((member) => !isMemberFieldSet(member, field)).map((member) => member.id);

/** The data an "Apply to empty lines" call sends for a column. */
export const getApplyPayload = (field, value) =>
  (field === 'debit_account' ? {debit_account: value} : {lines_custom_segments: {[field]: value}});

/** The distinct reporter ids of the members, in the order they first appear. */
export const getMemberUserIds = (header) =>
  [...new Set(getMembers(header).map((member) => member.reported_by_user_id).filter(Boolean))];

/**
 * How many debit lines NetSuite receives: one per distinct debit account and
 * line-level segment values (Department included), as the backend splits them.
 */
export const countDebitLines = (header) =>
  new Set(getMembers(header).map((member) => {
    const segments = Object.entries(member.line_segments ?? {})
      .filter(([, value]) => hasValue(value))
      .sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify([member.debit_account ?? '', segments]);
  })).size;

/** How many credit lines NetSuite receives: one per Location the members carry, or one. */
export const countCreditLines = (header) =>
  Math.max(1, new Set(getMembers(header).map((member) => member.line_segments?.[LOCATION_SEGMENT]).filter(hasValue)).size);

/** Whether the header has reached NetSuite successfully. */
export const isHeaderSynced = (header) => header?.sync_status === journalEntrySyncStatusConstants.SUCCESS;

/**
 * The member as the table shows it: the fields the header decides are copied down, so a sync
 * update to the header reaches its members on the next render. The Location is the header's only
 * when the company does not choose it per expense.
 * @param {Object} params
 * @param {Object} params.member - The member as the API returned it.
 * @param {Object} params.header - The formatted header.
 * @param {Object} [params.expense] - The member's expense, once loaded.
 * @param {Object} [params.vendor] - The member's vendor, once loaded.
 * @param {boolean} [params.inheritLocation] - False when Location is a line-level segment.
 */
export const formatMember = ({member, header, expense = null, vendor, inheritLocation = true}) => ({
  ...member,
  _record_type: suggestionRecordTypeConstants.JOURNAL_ENTRY,
  isAggregateMember: true,
  aggregate_header_id: header.id,
  expense,
  vendor,
  currency: header.currency,
  sync_status: header.sync_status,
  is_frozen: header.is_frozen,
  posting_date: header.posting_date,
  credit_account: header.credit_account,
  // A company that declares Location as a segment keeps the header's location there.
  location: inheritLocation ? header.location || header.custom_segments?.location : undefined,
});

/**
 * The rows the table nests under a header: placeholders while the members load, then the
 * explanatory row and the members.
 * @param {Object} header - Formatted header row.
 * @param {Array<Object>} members - Formatted members.
 */
export const getDisplayChildren = (header, members) => {
  const status = header.aggregate_members_status;
  if (status === AGGREGATE_MEMBERS_STATUS.LOADED) {
    const note = {
      id: getNoteRowId(header.id),
      isAggregateNote: true,
      aggregate_header_id: header.id,
      isFrozen: Boolean(header.is_frozen),
      isSynced: isHeaderSynced(header),
      isExpensesError: Boolean(header.aggregate_expenses_failed),
      memberCount: members.length,
      debitLineCount: countDebitLines(header),
      creditLineCount: countCreditLines(header),
      amount: header.amount,
      currency: header.currency,
    };
    return [note, ...members];
  }
  if (status === AGGREGATE_MEMBERS_STATUS.ERROR) {
    return [{id: getNoteRowId(header.id), isAggregateNote: true, isAggregateError: true, aggregate_header_id: header.id}];
  }
  return Array.from({length: SKELETON_ROW_COUNT}, (_, index) => ({
    id: getSkeletonRowId(header.id, index),
    isAggregateSkeleton: true,
  }));
};

/**
 * The row after an edit came back from the API. A member keeps its own shape and takes what the
 * user sent (its debit account, or line-level segment values), not what the response reads back;
 * a header keeps its fetched members.
 * @param {Object} row - The stored row.
 * @param {Object} updated - The formatted API response.
 * @param {Object} [header] - The header the row was found in, when it is one of its members.
 * @param {Object} [sent] - The data the edit sent.
 */
export const mergeUpdatedRow = (row, updated, header, sent = {}) => {
  if (header) {
    return {
      ...row,
      debit_account: sent.debit_account !== undefined ? sent.debit_account : row.debit_account,
      line_segments: sent.lines_custom_segments ? {...row.line_segments, ...sent.lines_custom_segments} : row.line_segments,
    };
  }
  if (!row?.aggregate_members) return updated;
  return {
    ...updated,
    aggregate_members: row.aggregate_members,
    aggregate_members_status: row.aggregate_members_status,
    aggregate_applying: row.aggregate_applying,
    aggregate_expenses_requested: row.aggregate_expenses_requested,
    aggregate_expenses_failed: row.aggregate_expenses_failed,
  };
};

/**
 * The header after its members failed to load. A fetch that was abandoned can fail after a newer
 * one succeeded: only a row that has not loaded takes the error. A row a reload reset to no status
 * takes it too, so the failed fetch is released and the row can be fetched again.
 */
export const markMembersError = (row) => (
  row.aggregate_members_status === AGGREGATE_MEMBERS_STATUS.LOADED
    ? row
    : {...row, aggregate_members_status: AGGREGATE_MEMBERS_STATUS.ERROR}
);

/** The header with a value set in a column of the given members. */
export const setMembersField = (header, ids, field, value) => {
  const updatedIds = new Set(ids);
  const setValue = (member) => (
    field === 'debit_account'
      ? {...member, debit_account: value}
      : {...member, line_segments: {...member.line_segments, [field]: value}}
  );
  return {
    ...header,
    aggregate_members: getMembers(header).map((member) => (updatedIds.has(member.id) ? setValue(member) : member)),
  };
};

/**
 * What the Vendor cell of a header says: nothing until its members are loaded, then "No vendor",
 * the one vendor's name, or "Multiple vendors".
 * @param {Object} params
 * @param {Object} params.header - The header row.
 * @param {Array<Object>} [params.vendors] - The vendors loaded so far.
 * @param {{none: string, many: string}} params.labels - The two words, already translated.
 */
export const getHeaderVendorLabel = ({header, vendors = [], labels}) => {
  if (header?.aggregate_members_status !== AGGREGATE_MEMBERS_STATUS.LOADED) return '';
  const ids = [...new Set(getMembers(header).map((member) => member.vendor_id).filter(hasValue))];
  if (!ids.length) return labels.none;
  if (ids.length > 1) return labels.many;
  return vendors.find((vendor) => String(vendor.vendor_id) === String(ids[0]))?.name ?? '';
};

/**
 * What "Apply to empty lines" did: how many members were set and which ones the API refused.
 * @param {Object} header - The header whose members were sent.
 * @param {{updated?: Array<{id: string}>, failed?: Array<{id: string, error: string}>}} response
 * @returns {{updatedIds: string[], failed: Array<{label: string, error: string}>}}
 */
export const summarizeApplyResult = (header, response) => {
  const members = getMembers(header);
  const labelOf = (id) => members.find((member) => member.id === id)?.memo || id;
  return {
    updatedIds: (response?.updated ?? []).map((entry) => entry.id),
    failed: (response?.failed ?? []).map((failure) => ({label: labelOf(failure.id), error: failure.error})),
  };
};

/** The ids open after a row is expanded or collapsed. */
export const toggleOpenId = (openIds, id, expanded) =>
  expanded ? [...new Set([...openIds, id])] : openIds.filter((openId) => openId !== id);

/** Whether a header's members are being fetched or have been fetched already. */
const isMembersRequestSettled = (row) =>
  row?.aggregate_members_status === AGGREGATE_MEMBERS_STATUS.LOADING
  || row?.aggregate_members_status === AGGREGATE_MEMBERS_STATUS.LOADED;

/**
 * Whether opening a header should fetch its members now: not while a fetch is in flight, not once
 * they are loaded, and not when this id was already requested and has not come back yet.
 * A header whose last fetch failed can be fetched again.
 */
export const canLoadMembers = ({row, requestedIds}) => !requestedIds.has(row.id) && !isMembersRequestSettled(row);

/**
 * The open headers that still need their members, for the pass that follows a render. A reload
 * replaces the rows and drops their members; an id already requested is left alone, so a render
 * that lands before the fetch has marked its row cannot start a second fetch.
 * @param {Object} params
 * @param {Array<Object>} params.rows - The tab's rows.
 * @param {string[]} params.openIds - The ids of the open rows.
 * @param {Set<string>} params.requestedIds - The ids whose fetch has started and not come back.
 * @returns {string[]} Header ids to fetch.
 */
export const pickMembersToLoad = ({rows, openIds, requestedIds}) =>
  rows
    .filter((row) => isAggregateHeader(row) && openIds.includes(row.id) && !row.aggregate_members_status && !requestedIds.has(row.id))
    .map((row) => row.id);

/** The ids whose fetch has come back, loaded or failed: they can be requested again later. */
export const getFinishedMemberIds = (rows) =>
  rows
    .filter((row) => isAggregateHeader(row)
      && (row.aggregate_members_status === AGGREGATE_MEMBERS_STATUS.LOADED
        || row.aggregate_members_status === AGGREGATE_MEMBERS_STATUS.ERROR))
    .map((row) => row.id);

/**
 * The requested ids that can be requested again: their row came back loaded or failed, or the row
 * is no longer in the table (a filter or a reload removed it mid-fetch), so nothing will ever
 * mark it finished.
 * @param {Object} params
 * @param {Array<Object>} params.rows - The tab's rows.
 * @param {Set<string>} params.requestedIds - The ids whose fetch has started and not come back.
 * @returns {string[]} Ids to release.
 */
export const getReleasedMemberIds = ({rows, requestedIds}) => {
  const finished = new Set(getFinishedMemberIds(rows));
  const present = new Set(rows.map((row) => row.id));
  return [...requestedIds].filter((id) => finished.has(id) || !present.has(id));
};

/**
 * The headers on screen to fetch members for ahead of any click, so a collapsed row already shows
 * its chips and users. Only a header with no status is picked: a failed one is not retried on its
 * own, it is fetched again when the row opens. The number of fetches in flight, counted from
 * `requestedIds`, never goes past `limit`. An empty header has nothing to fetch.
 * @param {Object} params
 * @param {Array<Object>} params.rows - The tab's rows.
 * @param {Set<string>} params.requestedIds - The ids whose fetch has started and not come back.
 * @param {number} [params.limit] - Most fetches in flight at once.
 * @returns {string[]} Header ids to fetch.
 */
export const pickMembersToPrefetch = ({rows, requestedIds, limit = MEMBER_PREFETCH_LIMIT}) => {
  const free = Math.max(0, limit - requestedIds.size);
  return rows
    .filter((row) => isAggregateHeader(row)
      && !row.aggregate_members_status
      && row.member_count !== 0
      && !requestedIds.has(row.id))
    .slice(0, free)
    .map((row) => row.id);
};

/**
 * The open headers whose members' expenses still have to be fetched. The members arrive on their
 * own, before or after the row opens; the expenses (the heavy call) wait for the row to be open.
 * A header is asked once: its row carries `aggregate_expenses_requested`, and `requestedIds` covers
 * the render that lands before that flag does. A header whose fetch failed (`aggregate_expenses_failed`)
 * is not asked again until the user retries.
 * @param {Object} params
 * @param {Array<Object>} params.rows - The tab's rows.
 * @param {string[]} params.openIds - The ids of the open rows.
 * @param {Set<string>} params.requestedIds - The ids whose expenses were just requested.
 * @returns {string[]} Header ids whose expenses to fetch.
 */
export const pickExpensesToLoad = ({rows, openIds, requestedIds}) =>
  rows
    .filter((row) => isAggregateHeader(row)
      && openIds.includes(row.id)
      && row.aggregate_members_status === AGGREGATE_MEMBERS_STATUS.LOADED
      && !row.aggregate_expenses_requested
      && !row.aggregate_expenses_failed
      && !requestedIds.has(row.id))
    .map((row) => row.id);

/** The ids whose expense request is now on the row, or whose row left the table. */
export const getReleasedExpenseIds = ({rows, requestedIds}) => {
  const requestedOnRow = new Set(rows.filter((row) => row.aggregate_expenses_requested).map((row) => row.id));
  const present = new Set(rows.map((row) => row.id));
  return [...requestedIds].filter((id) => requestedOnRow.has(id) || !present.has(id));
};

/** The keys of every row nested under a header in the table: the note, the placeholders and the members. */
export const getChildRowKeys = (rows) => new Set(
  rows.filter(isAggregateHeader).flatMap((header) => [
    getNoteRowId(header.id),
    ...Array.from({length: SKELETON_ROW_COUNT}, (_, index) => getSkeletonRowId(header.id, index)),
    ...getMembers(header).map((member) => member.id),
  ])
);

/**
 * The selection without the rows that sit under a header. Only a header is selected and synced,
 * so no path (a click, the checkbox, select all) may leave a nested row in the selection.
 * @param {string[]} keys - The selected row keys.
 * @param {Array<Object>} rows - The tab's rows.
 * @returns {string[]} The keys of rows that can be selected.
 */
export const keepSelectableKeys = (keys, rows) => {
  const childKeys = getChildRowKeys(rows);
  return keys.filter((key) => !childKeys.has(key));
};

/** Whether a member can be ticked: it sits in a header that can still change. */
export const isSelectableMember = (entry) => isAggregateMember(entry) && !entry.is_frozen;

const getMemberKeys = (rows) => new Set(rows.filter(isAggregateHeader).flatMap((header) => getMembers(header).map((member) => member.id)));

/**
 * What the selection holds: `'members'` (expenses of aggregated headers), `'rows'` (everything
 * else) or `null` when it is empty. The two never mix, so a bulk action always knows what it acts on.
 * @param {string[]} keys - The selected row keys.
 * @param {Array<Object>} rows - The tab's rows.
 * @returns {'members'|'rows'|null}
 */
export const getSelectionKind = (keys, rows) => {
  if (!keys?.length) return null;
  const memberKeys = getMemberKeys(rows);
  return keys.every((key) => memberKeys.has(key)) ? 'members' : 'rows';
};

/**
 * The selection cut down to one kind: members or ordinary rows, never both, and never a note or a
 * placeholder. A selection that already holds one kind keeps it; a mixed selection from an empty
 * start (select all) keeps the ordinary rows.
 * @param {string[]} keys - The keys the table reports.
 * @param {Array<Object>} rows - The tab's rows.
 * @param {string[]} [previousKeys] - The selection before this change.
 * @returns {string[]} The keys to select.
 */
export const keepOneKindOfKeys = (keys, rows, previousKeys = []) => {
  const memberKeys = getMemberKeys(rows);
  const nestedKeys = getChildRowKeys(rows);
  const rowKeys = keys.filter((key) => !nestedKeys.has(key));
  const selectedMembers = keys.filter((key) => memberKeys.has(key));
  if (!selectedMembers.length) return rowKeys;
  if (!rowKeys.length) return selectedMembers;
  return previousKeys.some((key) => memberKeys.has(key)) ? selectedMembers : rowKeys;
};

/**
 * The fields a bulk Classify can set on selected expenses of a header: the debit account and the
 * segments chosen per expense. Everything else belongs to the header.
 * @param {Array<{name: string}>} fields - Every field the Classify form offers.
 * @param {Array<Object>} memberSegments - The segments chosen per expense (`getMemberSegments`).
 * @returns {Array<{name: string}>}
 */
export const getMemberClassifyFields = (fields, memberSegments) => {
  const names = new Set(['debit_account', ...memberSegments.map((segment) => segment.name ?? segment.dataIndex)]);
  return fields.filter((field) => names.has(field.name));
};

/**
 * The rows after a bulk Classify set values on expenses of headers: each header that holds an
 * updated expense takes the debit account and the line-level segment values the call sent.
 * @param {Array<Object>} rows - The tab's rows.
 * @param {string[]} updatedIds - The ids the API updated.
 * @param {{debit_account?: string, lines_custom_segments?: Object}} payload - What the call sent.
 * @returns {Array<Object>} The rows, with the touched headers replaced.
 */
export const applyClassifyToMembers = (rows, updatedIds, payload) => {
  const updated = new Set(updatedIds);
  const values = [
    ...(payload.debit_account !== undefined ? [['debit_account', payload.debit_account]] : []),
    ...Object.entries(payload.lines_custom_segments ?? {}),
  ];
  return rows.map((row) => {
    if (!isAggregateHeader(row) || !getMembers(row).some((member) => updated.has(member.id))) return row;
    return values.reduce((header, [field, value]) => setMembersField(header, updatedIds, field, value), row);
  });
};
